import {
  addDoc,
  collection,
  doc,
  deleteDoc,
  DocumentSnapshot,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  startAfter,
  Timestamp,
  Unsubscribe,
  updateDoc,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import {httpsCallable} from 'firebase/functions';
import {db, functions} from './firebase';
import {
  FeedbackFilters,
  FeedbackMessage,
  FeedbackReport,
  FeedbackStatus,
  FeedbackTag,
  FeedbackTeam,
  ImageAttachment,
  PLATFORM_DISPLAY_NAMES,
} from '../types/feedback';
import {compareVersions} from './appVersionsService';

const FEEDBACK_COLLECTION = 'feedback';
const MESSAGES_SUBCOLLECTION = 'messages';
const PAGE_SIZE = 20;

export interface FeedbackPage {
  items: FeedbackReport[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}

// Check if we have client-side filters that need to be applied
function hasClientSideFilters(filters: FeedbackFilters): boolean {
    return !!(filters.searchQuery || filters.fromVersion);
}

// Resolve email to uid via Cloud Function
const emailToUidCache: Record<string, string | null> = {};

/** Une adresse peut echouer de deux facons tres differentes : aucun compte ne
 *  la porte, ou l'appel lui-meme n'a pas abouti. Les confondre revenait a
 *  afficher une liste vide dans les deux cas. */
export type EmailLookup =
    | { kind: 'found'; uid: string }
    | { kind: 'no-account' }
    | { kind: 'failed'; message: string };

export async function resolveEmailToUid(email: string): Promise<EmailLookup> {
    const key = email.trim().toLowerCase();
    if (key in emailToUidCache) {
        const cached = emailToUidCache[key];
        return cached ? { kind: 'found', uid: cached } : { kind: 'no-account' };
    }
    try {
        const fn = httpsCallable<{ email: string }, { uid: string | null }>(functions, 'findUserByEmail');
        const result = await fn({ email: key });
        emailToUidCache[key] = result.data.uid;
        return result.data.uid ? { kind: 'found', uid: result.data.uid } : { kind: 'no-account' };
    } catch (err) {
        // Ne RIEN mettre en cache ici : une panne passagere (demarrage a froid
        // de la fonction, coupure reseau, jeton admin pas encore propage)
        // rendait l'adresse introuvable pour toute la session, meme apres une
        // nouvelle tentative. Seul un rechargement de page la debloquait.
        return { kind: 'failed', message: err instanceof Error ? err.message : String(err) };
    }
}

// Apply client-side filters to items
function applyClientSideFilters(items: FeedbackReport[], filters: FeedbackFilters): FeedbackReport[] {
    let filteredItems = items;

    if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        filteredItems = filteredItems.filter(
            (item) =>
                item.message.toLowerCase().includes(searchLower) ||
                item.uid.toLowerCase().includes(searchLower) ||
                item.currentRoute.toLowerCase().includes(searchLower) ||
                item.platform.toLowerCase().includes(searchLower) ||
                (item.appVersion && item.appVersion.toLowerCase().includes(searchLower)) ||
                (item.deviceModel && item.deviceModel.toLowerCase().includes(searchLower))
        );
    }

    // Filter by version (show items with version >= fromVersion)
    if (filters.fromVersion) {
        filteredItems = filteredItems.filter(
            (item) => item.appVersion && compareVersions(item.appVersion, filters.fromVersion!) >= 0
        );
    }

    return filteredItems;
}

export async function getFeedbackList(
  filters: FeedbackFilters = {},
  lastDocument?: DocumentSnapshot
): Promise<FeedbackPage> {
    const baseConstraints: QueryConstraint[] = [];

  // Apply filters
  if (filters.category) {
      baseConstraints.push(where('category', '==', filters.category));
  }
  if (filters.status) {
      baseConstraints.push(where('status', '==', filters.status));
  }
  if (filters.platform) {
      baseConstraints.push(where('platform', '==', filters.platform));
  }
  if (filters.betaEnabled !== undefined) {
      baseConstraints.push(where('betaEnabled', '==', filters.betaEnabled));
  }
  if (filters.assignee) {
      baseConstraints.push(where('assignee', '==', filters.assignee));
  }
    if (filters.team) {
        baseConstraints.push(where('team', '==', filters.team));
    }
    if (filters.tag) {
        baseConstraints.push(where('tags', 'array-contains', filters.tag));
    }
  if (filters.uid) {
      baseConstraints.push(where('uid', '==', filters.uid));
  }
  if (filters.hasUnreadUserReply) {
      baseConstraints.push(where('hasUnreadUserReply', '==', true));
  }
  if (filters.startDate) {
      baseConstraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.startDate)));
  }
  if (filters.endDate) {
      baseConstraints.push(where('createdAt', '<=', Timestamp.fromDate(filters.endDate)));
  }

  // Always order by createdAt descending
    baseConstraints.push(orderBy('createdAt', 'desc'));

    const allFilteredItems: FeedbackReport[] = [];
    let currentLastDoc: DocumentSnapshot | null = lastDocument ?? null;
    let hasMoreFromFirestore = true;
    const maxIterations = 10; // Safety limit to prevent infinite loops
    let iterations = 0;

    // Keep fetching until we have enough items or no more data
    while (allFilteredItems.length < PAGE_SIZE && hasMoreFromFirestore && iterations < maxIterations) {
        iterations++;

        const constraints = [...baseConstraints, limit(PAGE_SIZE + 1)];
        if (currentLastDoc) {
            constraints.push(startAfter(currentLastDoc));
        }

        const q = query(collection(db, FEEDBACK_COLLECTION), ...constraints);
        const snapshot = await getDocs(q);

        if (snapshot.docs.length === 0) {
            hasMoreFromFirestore = false;
            break;
        }

        const items: FeedbackReport[] = [];
        snapshot.docs.slice(0, PAGE_SIZE).forEach((docSnap) => {
            items.push({
                id: docSnap.id,
                ...docSnap.data(),
            } as FeedbackReport);
        });

        // Update lastDoc to the last document we processed
        currentLastDoc = snapshot.docs[Math.min(snapshot.docs.length - 1, PAGE_SIZE - 1)];
        hasMoreFromFirestore = snapshot.docs.length > PAGE_SIZE;

        // Apply client-side filters
        const filteredItems = hasClientSideFilters(filters)
            ? applyClientSideFilters(items, filters)
            : items;

        allFilteredItems.push(...filteredItems);

        // If no client-side filters, we can stop after one iteration
        if (!hasClientSideFilters(filters)) {
            break;
        }
  }

    // Trim to PAGE_SIZE
    const resultItems = allFilteredItems.slice(0, PAGE_SIZE);

    // hasMore is true only if:
    // 1. We have more items than PAGE_SIZE after filtering, OR
    // 2. Firestore has more documents AND we filled the page (stopped early because we had enough)
    // If we didn't fill the page, it means we exhausted all matching results
    const hasMore = allFilteredItems.length > PAGE_SIZE ||
        (hasMoreFromFirestore && allFilteredItems.length >= PAGE_SIZE);

  return {
      items: resultItems,
      lastDoc: currentLastDoc,
      hasMore,
  };
}

export async function getFeedbackById(id: string): Promise<FeedbackReport | null> {
  const docRef = doc(db, FEEDBACK_COLLECTION, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as FeedbackReport;
}

// Fetch other feedbacks from the same user, excluding the current one.
// Returns at most `max` items, ordered by createdAt desc.
export async function getOtherFeedbacksByUid(
  uid: string,
  excludeId: string,
  max = 20
): Promise<FeedbackReport[]> {
  const q = query(
    collection(db, FEEDBACK_COLLECTION),
    where('uid', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(max + 1)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .filter((docSnap) => docSnap.id !== excludeId)
    .slice(0, max)
    .map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    } as FeedbackReport));
}

/**
 * Whether an admin has ever posted in this feedback's conversation.
 *
 * Reads a single document: we only need to know that one exists. Note that a
 * customer answered by phone or by direct e-mail shows up here as unanswered —
 * this is a hint, never a verdict.
 */
export async function hasAdminReply(feedbackId: string): Promise<boolean> {
  const messagesRef = collection(db, FEEDBACK_COLLECTION, feedbackId, MESSAGES_SUBCOLLECTION);
  const q = query(messagesRef, where('authorType', '==', 'admin'), limit(1));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Words that suggest the person is stuck on money or on access to something
 * they paid for — the cases where closing in silence costs the most.
 *
 * Deliberately crude and multilingual: it only decides how loudly we warn, so a
 * false positive costs a glance and a false negative costs nothing extra.
 */
const NEEDS_REPLY_PATTERN = new RegExp(
  [
    'rembours', 'refund', 'reembols', 'rimbors', 'erstatt',
    'factur', 'invoice', 'fattur', 'rechnung',
    'pay[ée]|payment|paiement|pagat|bezahl|betaal',
    'achat|acheté|purchase|bought|comprad|acquist|gekauft|gekocht',
    'abonnement|subscription|abbonamento|suscripci',
    'licence|license|licenza',
    'commande|order|pedido|ordine|bestell',
    'accès|acces|access|acceso|accesso|toegang',
  ].join('|'),
  'i'
);

/**
 * Whether closing this feedback as "Ignored" deserves a warning.
 *
 * Never blocks: closing without replying is a legitimate call — a comment may
 * need no answer at all, and the customer may already have been helped off the
 * tool. This only surfaces the cases a human would regret closing silently.
 */
export interface SilentCloseWarning {
  /** No admin message exists in the thread. */
  unanswered: boolean;
  /** The original message looks like it is about money or access. */
  looksImportant: boolean;
}

export async function checkSilentClose(
  feedbackId: string,
  status: FeedbackStatus | undefined
): Promise<SilentCloseWarning | null> {
  if (status !== 'ignored') return null;
  const snapshot = await getDoc(doc(db, FEEDBACK_COLLECTION, feedbackId));
  const data = snapshot.data();
  // Already binned: nothing new is being decided here.
  if (!data || data.status === 'ignored') return null;
  if (await hasAdminReply(feedbackId)) return null;
  return {
    unanswered: true,
    looksImportant: NEEDS_REPLY_PATTERN.test((data.message as string) ?? ''),
  };
}

export async function updateFeedbackStatus(
  id: string,
  status: FeedbackStatus,
  updatedBy: string
): Promise<void> {
  const docRef = doc(db, FEEDBACK_COLLECTION, id);
  await updateDoc(docRef, {
    status,
    updatedAt: Timestamp.now(),
    updatedBy,
  });
}

export async function updateFeedbackDetails(
  id: string,
  updates: {
    status?: FeedbackStatus;
      team?: FeedbackTeam | null;
    internalNote?: string;
    assignee?: string;
      tags?: FeedbackTag[];
  },
  updatedBy: string
): Promise<void> {
  const docRef = doc(db, FEEDBACK_COLLECTION, id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now(),
    updatedBy,
  });
}

export interface BulkUpdateOptions {
  status?: FeedbackStatus;
  team?: FeedbackTeam;
  addTags?: FeedbackTag[];
}

export async function bulkUpdateFeedback(
    ids: string[],
    updates: BulkUpdateOptions,
    updatedBy: string
): Promise<void> {
  const updatePromises = ids.map(async (id) => {
    const docRef = doc(db, FEEDBACK_COLLECTION, id);

    // For tags, we need to merge with existing tags
    if (updates.addTags && updates.addTags.length > 0) {
      const docSnap = await getDoc(docRef);
      const existingTags = docSnap.data()?.tags || [];
      const mergedTags = [...new Set([...existingTags, ...updates.addTags])];

      await updateDoc(docRef, {
        ...(updates.status && {status: updates.status}),
        ...(updates.team && {team: updates.team}),
        tags: mergedTags,
        updatedAt: Timestamp.now(),
        updatedBy,
      });
    } else {
      await updateDoc(docRef, {
        ...(updates.status && {status: updates.status}),
        ...(updates.team && {team: updates.team}),
        updatedAt: Timestamp.now(),
        updatedBy,
      });
    }
  });

  await Promise.all(updatePromises);
}

/**
 * Supprime definitivement une demande, et tout ce qui pend apres elle.
 *
 * FIRESTORE NE SUPPRIME PAS EN CASCADE. Effacer le document laisserait sa
 * sous-collection `messages` intacte : invisible dans la console, introuvable
 * par les requetes, et pourtant facturee. Les conversations partent donc
 * d'abord, une par une.
 *
 * CE QUI RESTE MALGRE TOUT : la capture d'ecran deposee dans Cloud Storage. La
 * retirer demanderait au navigateur un droit d'ecriture sur le depot que
 * l'interface n'a pas, et qu'on ne lui donnera pas pour cela. Le chemin figure
 * dans le document ; qui veut faire le menage complet passe par la console.
 *
 * La regle Firestore reserve deja ce geste aux administrateurs : cette
 * fonction ne fait qu'ouvrir la porte a ceux qui en ont la clef.
 */
export async function deleteFeedback(feedbackId: string): Promise<void> {
  const messagesRef = collection(db, FEEDBACK_COLLECTION, feedbackId, MESSAGES_SUBCOLLECTION);
  const messages = await getDocs(messagesRef);
  await Promise.all(messages.docs.map((m) => deleteDoc(m.ref)));
  await deleteDoc(doc(db, FEEDBACK_COLLECTION, feedbackId));
}

export async function getUniqueAssignees(): Promise<string[]> {
  // Load from team_members collection instead of scanning feedbacks
  const snapshot = await getDocs(collection(db, 'team_members'));
  const assignees = new Set<string>();
  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    // `canBeAssigned` absent vaut vrai : les fiches anterieures a ce champ
    // apparaissaient toutes dans cette liste, et les en retirer ferait
    // disparaitre des assignes existants sans que personne l'ait demande.
    // Les comptes de service, eux, le portent explicitement a faux.
    if (data.email && data.canBeAssigned !== false) {
      assignees.add(data.email as string);
    }
  });
  return Array.from(assignees).sort();
}

export function getUniquePlatforms(): Promise<string[]> {
  // Return lowercase keys that match Firestore values (android, ios, etc.)
  return Promise.resolve(Object.keys(PLATFORM_DISPLAY_NAMES));
}

export async function getUniqueVersions(): Promise<string[]> {
  const q = query(collection(db, FEEDBACK_COLLECTION), orderBy('createdAt', 'desc'), limit(500));
  const snapshot = await getDocs(q);
  const versions = new Set<string>();
  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.appVersion) {
      versions.add(data.appVersion);
    }
  });
  // Sort versions in descending order (newest first)
  // Versions with large patch numbers (>= 1000) are build numbers and should be sorted separately
  return Array.from(versions).sort((a, b) => {
    const partsA = a.split('.').map(Number);
    const partsB = b.split('.').map(Number);

    // Check if this looks like a build number (3rd segment >= 1000)
    const aHasBuildNumber = partsA.length >= 3 && partsA[2] >= 1000;
    const bHasBuildNumber = partsB.length >= 3 && partsB[2] >= 1000;

    // Versions with build numbers come after semantic versions
    if (aHasBuildNumber && !bHasBuildNumber) return 1;
    if (!aHasBuildNumber && bHasBuildNumber) return -1;

    // Compare normally within each group
    for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
      const pA = partsA[i] || 0;
      const pB = partsB[i] || 0;
      if (pA !== pB) return pB - pA;
    }
    return 0;
  });
}

// Get the latest detected app version per platform from feedback data
export async function getLatestVersionsByPlatform(): Promise<Record<string, string>> {
    const q = query(collection(db, FEEDBACK_COLLECTION), orderBy('createdAt', 'desc'), limit(1000));
    const snapshot = await getDocs(q);
    const {compareVersions} = await import('./appVersionsService');
    const {PLATFORM_MAP} = await import('../types/feedback');

    const latestByPlatform: Record<string, string> = {};

    snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.platform && data.appVersion) {
            const normalizedPlatform = PLATFORM_MAP[data.platform] || data.platform.toLowerCase();
            const currentVersion = data.appVersion;

            if (!latestByPlatform[normalizedPlatform]) {
                latestByPlatform[normalizedPlatform] = currentVersion;
            } else {
                if (compareVersions(currentVersion, latestByPlatform[normalizedPlatform]) > 0) {
                    latestByPlatform[normalizedPlatform] = currentVersion;
                }
            }
        }
    });

    return latestByPlatform;
}

// Message functions for feedback conversations

export function subscribeToMessages(
  feedbackId: string,
  onMessages: (messages: FeedbackMessage[]) => void
): Unsubscribe {
  const messagesRef = collection(db, FEEDBACK_COLLECTION, feedbackId, MESSAGES_SUBCOLLECTION);
  const q = query(messagesRef, orderBy('createdAt', 'asc'));

  return onSnapshot(q, (snapshot) => {
    const messages: FeedbackMessage[] = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    } as FeedbackMessage));
    onMessages(messages);
  });
}

export async function getMessages(feedbackId: string): Promise<FeedbackMessage[]> {
  const messagesRef = collection(db, FEEDBACK_COLLECTION, feedbackId, MESSAGES_SUBCOLLECTION);
  const q = query(messagesRef, orderBy('createdAt', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  } as FeedbackMessage));
}

export async function sendAdminMessage(
  feedbackId: string,
  content: string,
  adminEmail: string,
  adminName?: string,
  images?: ImageAttachment[],
): Promise<string> {
  const messagesRef = collection(db, FEEDBACK_COLLECTION, feedbackId, MESSAGES_SUBCOLLECTION);

  const message: Record<string, unknown> = {
    authorType: 'admin' as const,
    authorId: adminEmail,
    authorName: adminName || adminEmail,
    content,
    createdAt: Timestamp.now(),
  };
  if (images && images.length > 0) message.images = images;

  const docRef = await addDoc(messagesRef, message);

  // Update the feedback document with conversation metadata
  const feedbackRef = doc(db, FEEDBACK_COLLECTION, feedbackId);
  await updateDoc(feedbackRef, {
    hasUnreadAdminReply: true,
    hasUnreadUserReply: false,
    lastMessageAt: Timestamp.now(),
  });

  return docRef.id;
}

export async function editMessage(
  feedbackId: string,
  messageId: string,
  content: string,
  images?: ImageAttachment[],
): Promise<void> {
  const messageRef = doc(db, FEEDBACK_COLLECTION, feedbackId, MESSAGES_SUBCOLLECTION, messageId);
  // Always write `images` (possibly empty) and clear legacy `imageUrl` so edits
  // that remove images are persisted correctly.
  await updateDoc(messageRef, {
    content,
    images: images ?? [],
    imageUrl: null,
    editedAt: serverTimestamp(),
  });
}

export async function markUserRepliesAsRead(feedbackId: string): Promise<void> {
  const feedbackRef = doc(db, FEEDBACK_COLLECTION, feedbackId);
  await updateDoc(feedbackRef, {
    hasUnreadUserReply: false,
  });
}

// User purchases

// License type IDs from LicenseType enum
const LICENSE_TYPE_LABELS: Record<number, string> = {
  [-1]: 'Unknown',
  0: 'Trial',
  1: 'Purchase',
  2: 'Developer',
  3: 'Demo',
  4: 'Free',
};

// Payment platform IDs from PaymentPlatform enum
const PAYMENT_PLATFORM_LABELS: Record<number, string> = {
  [-1]: 'Unknown',
  1: 'PayPal',
  2: 'App Store',
  3: 'External',
  4: 'Credit Card',
  5: 'Stripe',
  6: 'Google Play',
};

// Purchase origin IDs from PurchaseOrigin enum
const PURCHASE_ORIGIN_LABELS: Record<number, string> = {
  [-1]: 'Unknown',
  1: 'Product Info',
  2: 'Store List',
  3: 'Viewer Drawer',
  4: 'Progress Menu',
  5: 'Mantano Website',
  6: 'Assimil Applivre',
  7: 'Assimil Website',
  8: 'Webapp',
  9: 'Lesson Completed',
  10: 'Market',
  11: 'License Info',
};

export interface UserPurchase {
  ean: string;
  title: string;
  coverUrl?: string;
  licenseType: string;
  isTrial: boolean;
  paymentDate?: Date;
  paymentPlatform?: string;
  purchaseOrigin?: string;
}

export async function getUserPurchases(uid: string): Promise<UserPurchase[]> {
  const purchasesRef = collection(db, 'user', uid, 'purchase');
  const snapshot = await getDocs(purchasesRef);

  const purchases: UserPurchase[] = [];
  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data();
    // Title is at position 0 in the document (field name "0")
    const title = data['0'] as string | undefined;
    const coverFile = data['coverFile'] as { fileName?: string } | undefined;

    // License info
    const license = data['license'] as {
      licenseTypeId?: number;
      payment?: {
        paymentDate?: string;
        paymentPlatformId?: number;
        purchaseOriginId?: number;
      };
    } | undefined;

    const licenseTypeId = license?.licenseTypeId ?? -1;
    const isTrial = licenseTypeId === 0;
    const licenseType = LICENSE_TYPE_LABELS[licenseTypeId] || 'Unknown';

    // Payment info
    const payment = license?.payment;
    let paymentDate: Date | undefined;
    if (payment?.paymentDate) {
      paymentDate = new Date(payment.paymentDate);
    }
    const paymentPlatformId = payment?.paymentPlatformId ?? -1;
    const paymentPlatform = PAYMENT_PLATFORM_LABELS[paymentPlatformId];
    const purchaseOriginId = payment?.purchaseOriginId ?? -1;
    const purchaseOrigin = PURCHASE_ORIGIN_LABELS[purchaseOriginId];

    if (title) {
      purchases.push({
        ean: docSnap.id,
        title,
        coverUrl: coverFile?.fileName,
        licenseType,
        isTrial,
        paymentDate,
        paymentPlatform,
        purchaseOrigin,
      });
    }
  });

  return purchases.sort((a, b) => a.title.localeCompare(b.title));
}

// User info from Firebase Auth

export interface UserInfo {
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  creationTime: string | null;
  lastSignInTime: string | null;
  providerIds: string[];
}

interface GetUserInfoResponse {
  success: boolean;
  error?: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  emailVerified?: boolean;
  creationTime?: string | null;
  lastSignInTime?: string | null;
  providerIds?: string[];
}

export async function getUserInfo(uid: string): Promise<UserInfo | null> {
  try {
    const getUserInfoFn = httpsCallable<{ uid: string }, GetUserInfoResponse>(functions, 'getUserInfo');
    const result = await getUserInfoFn({ uid });

    if (!result.data.success) {
      console.warn('getUserInfo failed:', result.data.error);
      return null;
    }

    return {
      email: result.data.email ?? null,
      displayName: result.data.displayName ?? null,
      photoURL: result.data.photoURL ?? null,
      emailVerified: result.data.emailVerified ?? false,
      creationTime: result.data.creationTime ?? null,
      lastSignInTime: result.data.lastSignInTime ?? null,
      providerIds: result.data.providerIds ?? [],
    };
  } catch (error) {
    console.error('Error calling getUserInfo:', error);
    return null;
  }
}
